import {
  CategoryState,
  RoomPersonalizationAvailableItemState,
  useGetPersonalizationAvailableItemsQuery,
  useGetPersonalizationCategoriesQuery,
} from '@/redux';
import { useMemo } from 'react';
import { ProjectHomeIdentifiersProps } from './OfferedItemDetails';

export type OfferedItemLookup = {
  id: string;
  name: string;
  price?: number;
  tbd?: boolean;
  category: string;
  subcategory?: string;
  subcategory2?: string;
};

type CategoryLookup = {
  category: string;
  subcategory?: string;
  subcategory2?: string;
};

function buildCategoryLookup(
  categories: CategoryState[],
): Record<string, CategoryLookup> {
  const lookup: Record<string, CategoryLookup> = {};

  function traverse(node: CategoryState, parents: string[] = []) {
    const [cat, sub] = parents;

    lookup[node.id] = {
      category: cat ?? node.name,
      subcategory: cat ? sub ?? node.name : undefined,
      subcategory2: cat && sub ? node.name : undefined,
    };

    node.children?.forEach((child) => {
      traverse(child as unknown as CategoryState, [...parents, node.name]);
    });
  }

  categories.forEach((c) => traverse(c));
  return lookup;
}

function mapItemStateToItem(
  itemState: RoomPersonalizationAvailableItemState,
  lookup: Record<string, CategoryLookup>,
): OfferedItemLookup | false {
  const resolved = lookup[itemState.categoryId];

  if (!resolved) return false;

  return {
    id: itemState.id,
    name: itemState.name,
    price: itemState.pricing?.totalResidentPrice,
    tbd: itemState.pricing?.tbd,
    category: resolved.category,
    subcategory: resolved.subcategory,
    subcategory2: resolved.subcategory2,
  };
}

type DataSourceProps = ProjectHomeIdentifiersProps & {
  stepId: string;
};

export function useOfferedItemsDataSource(options: DataSourceProps): {
  items: OfferedItemLookup[];
  categories: string[];
} {
  const { projectId, homeId, stepId } = options;

  const { data: itemStates = [] } = useGetPersonalizationAvailableItemsQuery({
    projectId,
    homeId,
    stepId,
  });

  const { data: categoryStates = [] } = useGetPersonalizationCategoriesQuery({
    homeId,
    projectId,
    stepId,
    floorplanPersonalization: true,
  });

  return useMemo(() => {
    const lookup = buildCategoryLookup(categoryStates);
    const items = itemStates
      .map((i) => mapItemStateToItem(i, lookup))
      .filter((x) => x !== false)
      .sort((a, b) => (a ? a.name : '').localeCompare(b ? b.name : ''));

    const categories = categoryStates
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b));

    return { items: items as OfferedItemLookup[], categories };
  }, [itemStates, categoryStates]);
}
